/**
 * Tiny hover tooltip. Portal-rendered so it can escape `overflow:hidden`
 * scroll containers (the labels rail, the reader scroll area, etc.) and
 * positions itself relative to the trigger's bounding rect.
 *
 * Usage:
 *   <Tooltip content="Mark as read"><button>…</button></Tooltip>
 *
 * Appears after a 250ms hover delay (matches Gmail/Linear feel) and
 * disappears immediately on mouseleave or click.
 */
import { cloneElement, isValidElement, useEffect, useRef, useState, type ReactElement, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

export type TooltipSide = "top" | "bottom" | "left" | "right";

interface TooltipProps {
  content: ReactNode;
  /** Side of the trigger to render the tooltip on. Defaults to "bottom". */
  side?: TooltipSide;
  /** Delay before showing (ms). Defaults to 250. */
  delay?: number;
  /** When false, render children alone (escape hatch for places that
   *  conditionally need a tooltip). */
  enabled?: boolean;
  children: ReactElement;
}

const SHOW_DELAY = 250;
const OFFSET = 8;

export function Tooltip({
  content,
  side = "bottom",
  delay = SHOW_DELAY,
  enabled = true,
  children,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLElement | null>(null);
  const timerRef = useRef<number | null>(null);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const computePos = () => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    let x = 0, y = 0;
    switch (side) {
      case "top":    x = r.left + r.width / 2; y = r.top - OFFSET; break;
      case "bottom": x = r.left + r.width / 2; y = r.bottom + OFFSET; break;
      case "left":   x = r.left - OFFSET;      y = r.top + r.height / 2; break;
      case "right":  x = r.right + OFFSET;     y = r.top + r.height / 2; break;
    }
    setPos({ x, y });
  };

  const show = () => {
    if (!enabled) return;
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      computePos();
      setOpen(true);
    }, delay);
  };

  const hide = () => {
    clearTimer();
    setOpen(false);
  };

  useEffect(() => clearTimer, []);

  // Hide when the page scrolls — otherwise the tooltip stays anchored at
  // its old absolute coords while the trigger has moved.
  useEffect(() => {
    if (!open) return;
    const onScroll = () => hide();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  if (!isValidElement(children)) return children;

  // Inject ref + event handlers on the trigger child without losing its own.
  const child = children as ReactElement<{
    ref?: React.Ref<HTMLElement>;
    onMouseEnter?: (e: React.MouseEvent) => void;
    onMouseLeave?: (e: React.MouseEvent) => void;
    onFocus?: (e: React.FocusEvent) => void;
    onBlur?: (e: React.FocusEvent) => void;
    onClick?: (e: React.MouseEvent) => void;
  }>;
  const original = child.props;
  const clone = cloneElement(child, {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      // Forward the original ref if there was one.
      const origRef = (child as unknown as { ref?: React.Ref<HTMLElement> }).ref;
      if (typeof origRef === "function") origRef(node);
      else if (origRef && typeof origRef === "object") {
        (origRef as React.MutableRefObject<HTMLElement | null>).current = node;
      }
    },
    onMouseEnter: (e: React.MouseEvent) => { show(); original.onMouseEnter?.(e); },
    onMouseLeave: (e: React.MouseEvent) => { hide(); original.onMouseLeave?.(e); },
    onFocus: (e: React.FocusEvent) => { show(); original.onFocus?.(e); },
    onBlur: (e: React.FocusEvent) => { hide(); original.onBlur?.(e); },
    onClick: (e: React.MouseEvent) => { hide(); original.onClick?.(e); },
  });

  const transform =
    side === "top"    ? "-translate-x-1/2 -translate-y-full" :
    side === "bottom" ? "-translate-x-1/2 translate-y-0" :
    side === "left"   ? "-translate-x-full -translate-y-1/2" :
    "-translate-y-1/2";

  return (
    <>
      {clone}
      {open && enabled && typeof document !== "undefined" && createPortal(
        <div
          role="tooltip"
          className={cn(
            "pointer-events-none fixed z-[9999] whitespace-nowrap rounded-md bg-zinc-800 px-2 py-1",
            "text-[11px] font-medium text-zinc-100 shadow-lg shadow-black/40 ring-1 ring-zinc-700/60",
            "animate-fade-in",
            transform
          )}
          style={{ left: pos.x, top: pos.y }}
        >
          {content}
        </div>,
        document.body
      )}
    </>
  );
}
